import { Building2, Users, Wallet, FileText, Workflow, BarChart3, HardHat, Boxes } from "lucide-react";
import { IMG } from "@/data/site";
import { Reveal, Overline, GlassCard, SectionHeader } from "@/components/site/primitives";
import { OButton } from "@/components/site/OButton";

const MODULES = [
  { icon: Building2, title: "Projects", text: "Plan, track and connect every project in one intelligent view." },
  { icon: Users, title: "People", text: "Teams, roles and responsibilities across the whole business." },
  { icon: Wallet, title: "Finance", text: "Budgets, costs and cashflow understood in real time." },
  { icon: FileText, title: "Documents", text: "Drawings, contracts and records — connected, not scattered." },
  { icon: Workflow, title: "Workflows", text: "Approvals and operations automated across the organisation." },
  { icon: HardHat, title: "Field & Site", text: "Bring site reality into the system as it happens." },
  { icon: Boxes, title: "Operations", text: "Procurement, resources and execution in one flow." },
  { icon: BarChart3, title: "Business Intelligence", text: "Every decision informed by the full picture." },
];

const CAPABILITIES = [
  "Industry-specific AI trained for construction business",
  "A single connected data foundation across projects",
  "Intelligent workflows that reduce manual coordination",
  "Real-time visibility from site to boardroom",
  "Decisions guided by context, not guesswork",
  "Enterprise-grade security and scalability",
];

export function ConstructonsStory() {
  return (
    <div className="mt-28">
      {/* Positioning statement */}
      <section className="oraya-container">
        <Reveal>
          <div className="max-w-4xl">
            <Overline>Why ConstructONS.AI</Overline>
            <h2 className="display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1] text-ink mt-6">
              Construction runs on dozens of disconnected tools.
              <span className="block text-subtle mt-3">ConstructONS.AI replaces them with one intelligent operating system.</span>
            </h2>
          </div>
        </Reveal>
      </section>

      {/* Modules grid */}
      <section className="oraya-container py-24 md:py-32">
        <Reveal>
          <SectionHeader overline="One system" title="Everything the business runs on, connected." subtitle="ConstructONS.AI brings projects, people, finance, documents, workflows and operations into a single continuous system — with AI at the centre." />
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {MODULES.map((m, i) => (
            <Reveal key={m.title} delay={(i % 4) * 0.06}>
              <GlassCard variant="medium" hover className="p-7 h-full" data-testid={`constructons-module-${i}`}>
                <m.icon className="w-5 h-5 text-ink" strokeWidth={1.4} />
                <h3 className="display text-lg font-medium text-ink mt-6">{m.title}</h3>
                <p className="text-sm text-subtle mt-2 leading-relaxed">{m.text}</p>
              </GlassCard>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Feature block 1 */}
      <section className="oraya-container">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-6">
            <div className="relative rounded-3xl overflow-hidden glass-heavy min-h-[300px] md:min-h-[420px]">
              <img src={IMG.constructonsDashboard} alt="ConstructONS.AI intelligent dashboard visualization" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-oraya-black/60 to-transparent" />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-5 md:col-start-8">
            <Overline>Intelligence, not dashboards</Overline>
            <h3 className="display text-2xl sm:text-3xl font-light text-ink mt-5 leading-snug">The system understands the business, then guides it.</h3>
            <p className="text-subtle mt-4 leading-relaxed">
              ConstructONS.AI doesn't just store information — it understands context, surfaces what matters and
              recommends the next best action across every project.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Feature block 2 */}
      <section className="oraya-container py-24 md:py-32">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <Reveal className="md:col-span-5">
            <Overline>From site to boardroom</Overline>
            <h3 className="display text-2xl sm:text-3xl font-light text-ink mt-5 leading-snug">One continuous flow of intelligence.</h3>
            <p className="text-subtle mt-4 leading-relaxed">
              What happens on site flows into finance, documents and decisions instantly — so the whole business
              operates from a single, current source of truth.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="md:col-span-6 md:col-start-7">
            <div className="relative rounded-3xl overflow-hidden glass-heavy min-h-[300px] md:min-h-[420px]">
              <img src={IMG.constructonsField} alt="ConstructONS.AI connecting construction site data" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-oraya-black/60 to-transparent" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Capabilities */}
      <section className="oraya-container">
        <Reveal>
          <SectionHeader overline="Capabilities" title="Built as a system, from day one." />
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={i} delay={(i % 2) * 0.06}>
              <div className="flex items-start gap-4 py-4 border-b border-hairline/[0.08]">
                <span className="text-oraya-accent mt-1">—</span>
                <span className="text-ink">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA band (always dark) */}
      <section data-theme="dark" className="relative mt-28 py-28 md:py-36 overflow-hidden border-y border-hairline/[0.06] bg-surface text-ink">
        <div className="grain" />
        <div className="oraya-container relative z-10 text-center">
          <Reveal>
            <h2 className="display text-3xl sm:text-5xl font-light tracking-tight text-gradient-mist">Bring intelligence to your construction business.</h2>
            <p className="text-subtle mt-6 max-w-xl mx-auto">ConstructONS.AI is an ORAYA system. Talk to us about bringing it to your organisation.</p>
            <div className="mt-9"><OButton to="/contact" variant="primary" icon data-testid="constructons-cta">Talk to ORAYA</OButton></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
